import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    listAccessRequests, approveAccessRequest, denyAccessRequest, type AccessRequest,
} from '../packages/api-client/access-requests-api';
import { getUsers, type User } from '../packages/api-client/pm-api';
import { getErrorMessage } from '../packages/api-client';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageSkeleton } from '../components/Skeleton';

/**
 * Approver inbox: pending access requests the current user can decide. Linked
 * from the `access_requested` notification.
 */
export function AccessRequestsPage() {
    usePageTitle('Access requests');
    const [requests, setRequests] = useState<AccessRequest[]>([]);
    const [userMap, setUserMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        listAccessRequests()
            .then(setRequests)
            .catch(() => { /* empty list renders the empty state */ })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getUsers(200)
            .then(us => setUserMap(Object.fromEntries(us.map((u: User) =>
                [u.id, (u.first_name || u.last_name) ? `${u.first_name} ${u.last_name}`.trim() : u.email]))))
            .catch(() => {});
    }, []);

    async function decide(id: string, approve: boolean) {
        setBusy(id);
        try {
            await (approve ? approveAccessRequest(id) : denyAccessRequest(id));
            setRequests(prev => prev.filter(r => r.id !== id));
            toast.success(approve ? 'Access granted' : 'Request denied');
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setBusy(null);
        }
    }

    const requesterName = (id: string) => userMap[id] || id.slice(0, 8) + '…';

    if (loading) return <PageSkeleton />;

    return (
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem' }}>
            <h1 style={{ fontSize: 'var(--font-size-xl)', marginBottom: '1rem' }}>Access requests</h1>
            {requests.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)' }}>No pending access requests.</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {requests.map(r => (
                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem 1rem', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                    {requesterName(r.user_id)} requested access to a {r.resource_type}
                                </div>
                                {r.note && <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginTop: 2 }}>“{r.note}”</div>}
                                <Link to={`/${r.resource_type}s/${r.resource_id}`} style={{ fontSize: 'var(--font-size-xs)' }}>
                                    Open {r.resource_type} ↗
                                </Link>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                <button className="btn btn-secondary btn-sm" disabled={busy === r.id} onClick={() => decide(r.id, false)}>Deny</button>
                                <button className="btn btn-primary btn-sm" disabled={busy === r.id} onClick={() => decide(r.id, true)}>Approve</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
